import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiSearch, FiChevronRight, FiCheck, FiCheckCircle, FiUserX, FiLock, FiClock, FiHeart, FiUsers } from 'react-icons/fi'
import { HiAcademicCap } from 'react-icons/hi2'
import { PublicTeacherListService, CheckFeedbackStatusService } from '../../services/TeacherFeedbackServices'
import { Hero, Card, SectionLabel, EmptyState, SkeletonCard, Spinner, FooterBand, TEACHER_ACCENTS, BRAND_NAVY, BG_SOFT } from './components/PublicHeader'

export default function PublicFeedbackTeacherList() {
  const navigate = useNavigate()
  const [student, setStudent] = useState(null)
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [checkingId, setCheckingId] = useState(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    const raw = sessionStorage.getItem('feedback_student')
    if (!raw) {
      navigate('/parent_feedback')
      return
    }
    const parsed = JSON.parse(raw)
    setStudent(parsed)
    fetchTeachers(parsed.studentId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function fetchTeachers(studentId) {
    setLoading(true)
    // Passing the student lets the backend annotate each teacher with
    // feedback_submitted for this month, which drives both the progress
    // bar and the per-row "Submitted" badge below.
    const result = await PublicTeacherListService(studentId)
    if (result?.data?.status) {
      setTeachers(result.data.data || [])
    }
    setLoading(false)
  }

  const filteredTeachers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return teachers
    return teachers.filter(t =>
      t.name?.toLowerCase().includes(q) ||
      t.expertise?.toLowerCase().includes(q) ||
      t.subjects?.some(s => s.toLowerCase().includes(q))
    )
  }, [teachers, search])

  // Progress reflects every teacher, not just the ones the search box is
  // currently showing.
  const reviewedCount = teachers.filter(t => t.feedback_submitted).length

  async function handleGiveFeedback(teacher) {
    if (checkingId) return
    setCheckingId(teacher._id)
    const studentId = JSON.parse(sessionStorage.getItem('feedback_student'))?.studentId
    const status = await CheckFeedbackStatusService(studentId, teacher._id)
    setCheckingId(null)

    sessionStorage.setItem('feedback_teacher', JSON.stringify(teacher))
    if (status?.data?.already_submitted) {
      sessionStorage.setItem('feedback_existing', JSON.stringify(status.data.data))
    } else {
      sessionStorage.removeItem('feedback_existing')
    }
    navigate(`/parent_feedback/form/${teacher._id}`)
  }

  return (
    <div className="min-h-screen bg-white">
      <Hero icon={HiAcademicCap} title="Select a Teacher to Review" subtitle="Your feedback helps us improve teaching quality and create a better learning experience for every student." />

      <div className="max-w-lg mx-auto px-4 -mt-7 pb-2 relative">
        <Card className="!p-3.5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-[15px] font-bold text-white flex-shrink-0" style={{ background: BRAND_NAVY }}>
              {student?.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0 flex-1 -mt-1">
              <p className="text-[10.5px] text-gray-400 font-semibold  tracking-wide">Providing feedback for</p>
              <p className="font-bold text-gray-900 text-[14.5px] capitalize truncate">{student?.name || 'your child'}</p>
            </div>
            {student?.standard && (
              <span className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full flex-shrink-0" style={{ background: '#e8f0fe', color: '#3b6fd9' }}>
                <HiAcademicCap size={14} /> {student.standard}
              </span>
            )}
          </div>
        </Card>
      </div>

      {!loading && teachers.length > 0 && (
        <div className="max-w-lg mx-auto px-4 pt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-bold text-gray-500">Feedback Progress</span>
            <span className="text-[11px] font-extrabold" style={{ color: BRAND_NAVY }}>{reviewedCount} of {teachers.length} Teachers Reviewed</span>
          </div>
          <div className="flex gap-1">
            {teachers.map((t, i) => (
              <span
                key={t._id}
                className="h-1.5 flex-1 rounded-full transition-colors"
                style={{ background: i < reviewedCount ? '#1a9d5c' : '#e5e9f2' }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 pt-5 pb-2">
        <SectionLabel icon={FiUsers} title="Choose a Teacher" subtitle="Select the teacher you would like to review." />
      </div>

      <div className="max-w-lg mx-auto px-4 pb-2">
        {/* {!loading && teachers.length > 3 && (
          <div className="mb-4 relative">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or subject…"
              className="w-full border-2 border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-[13.5px] focus:outline-none focus:border-[#14213d] bg-white transition-colors"
            />
          </div>
        )} */}

        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </div>
        )}

        {!loading && (
          <div className="space-y-3">
            {filteredTeachers.map((t, i) => {
              const accent = TEACHER_ACCENTS[i % TEACHER_ACCENTS.length]
              const submitted = !!t.feedback_submitted
              return (
                <div
                  key={t._id}
                  onClick={() => handleGiveFeedback(t)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleGiveFeedback(t) }}
                  className="cursor-pointer"
                >
                  <Card className={`!p-3 transition-colors ${submitted ? '!border-[#bfead0]' : ''}`} style={submitted ? { background: '#f3fbf6' } : undefined}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center text-lg font-bold flex-shrink-0" style={{ background: accent.avatar }}>
                        {t.profile_photo
                          ? <img src={`${process.env.REACT_APP_AWS_IMAGE_URL || ''}${t.profile_photo}`} alt={t.name} className="w-full h-full object-cover" />
                          : t.name?.[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-[700] text-gray-900 text-[14px] truncate">{t.name}</p>
                        {t.expertise && (
                          <p className="word-break text-[9px] font-bold px-2.5 py-0.5 rounded-[5px] mt-1" style={{ background: accent.pillBg, color: accent.pillText }}>
                            {t.expertise}
                          </p>
                        )}
                        {submitted && (
                          <p className="text-[10.5px] font-bold flex items-center gap-1 mt-1.5" style={{ color: '#1a9d5c' }}>
                            <FiCheckCircle size={12} /> Feedback Submitted
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {checkingId === t._id ? <Spinner /> : submitted ? (
                          <span className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ background: '#1a9d5c' }}>
                            <FiCheck size={14} />
                          </span>
                        ) : (
                          <FiChevronRight className="text-gray-400" size={18} />
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              )
            })}

            {filteredTeachers.length === 0 && teachers.length > 0 && (
              <EmptyState icon={FiSearch} title="No teachers match your search" subtitle="Try a different name or subject." />
            )}
            {teachers.length === 0 && (
              <EmptyState icon={FiUserX} title="No teachers available right now" subtitle="Please check back later." />
            )}
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto px-4 py-5">
        <div className="rounded-xl flex border border-gray-100 px-4 py-3 flex items-center justify-between gap-2" style={{ background: BG_SOFT }}>
          <div className="text-gray-500 w-[100%] flex border-r pr-2 items-center gap-1.5">
            <FiLock size={20} className='bg-slate-200 flex-shrink-0 flex items-center h-max p-[4px] rounded-full' style={{ color: BRAND_NAVY }} />
            <span className='text-[10px]'>Your feedback is <span className="font-bold" style={{ color: BRAND_NAVY }}>confidential</span> and used only for improvement purposes.</span>
          </div>
          <div className="text-[11.5px] w-[100%] text-gray-500 flex items-center gap-1.5 whitespace-nowrap">
            <FiClock size={20} className='bg-slate-200 flex-shrink-0 flex items-center h-max p-[4px] rounded-full' style={{ color: BRAND_NAVY }} />
            <span className='text-[10px]'>Takes only <span className="font-bold" style={{ color: BRAND_NAVY }}>2 minutes!</span></span>
          </div>
        </div>
      </div>

      <FooterBand
        icon={FiHeart}
        title="Thank you for being a part of our learning community!"
        subtitle="Together, we create a better future for our children."
      />
    </div>
  )
}
